<template>

    <div class="row">
        <div class="col-lg-8 offset-lg-2 col-xl-6 offset-xl-3">

            <div v-if="this.showPrepopMessage" class="">

                <div v-if="this.affiliateLogoUrl" class="text-center mt-2 mb-2">
                    <small class="text-muted">{{ this.getTranslatedText('__you_were_referred_by__') + '...' }}</small><br>
                    <img :src="this.affiliateLogoUrl" loading="lazy" alt="partner logo" class="img-fluid my-2" style="max-width: 120px; max-height: 80px;"/>
                </div>

                <svg xmlns="http://www.w3.org/2000/svg" style="display: none;">
                    <symbol id="info-fill" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                    </symbol>
                </svg>
                <div class="mt-0 mb-4">
                    <div class="alert alert-success text-center" role="alert">
                        <svg class="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Info:"><use xlink:href="#info-fill"/></svg>
                        <span v-html="this.getTranslatedText('__from_prepop_get_loan__')"></span>
                    </div>
                </div>
            </div>

            <div class="card shadow my-2 bg-body rounded h-auto py-4 px-3 py-md-4 px-md-5">

                <LoadingAnimation v-if="this.loadingAnimation" :showLcLogo="this.showLcLogo"
                                  :showAffiliateLogo="this.showAffiliateLogo"
                                  :brandLogo="this.projectBrand"
                                  :message="this.applicationSettings.loadingAnimationMessage ?? null"
                />

                <div v-show="!this.loadingAnimation" class="">

                    <Notifications :notifications="this.applicationSettings.notifications" />

                    <template v-if="showCompleteFlow">
                        <!-- application + account creation + verification -->
                        <InitialFlow v-if="showInitialFlow" v-model:application-settings="applicationSettings"
                            v-model:component-settings="initialFlowSettings" />

                        <RegistrationFlow v-if="showRegistrationFlow" v-model:application-settings="applicationSettings"
                            v-model:component-settings="registrationFlowSettings" />

                        <RegistrationFlowForMembers v-if="showRegistrationFlowForMembers" v-model:application-settings="applicationSettings"
                            v-model:component-settings="registrationFlowSettingsForMembers" />

                        <VerificationFlow v-if="showVerificationFlow" v-model:application-settings="applicationSettings"
                                          v-model:component-settings="verificationFlowSettings" :skipVerification="this.skipVerification || (!!this.app.forcePhoneVerification && !this.app.isPhoneVerified)"/>
                    </template>

                    <FinalFlow v-if="showFinalFlow" v-model:application-settings="applicationSettings"
                        v-model:component-settings="finalFlowSettings" />

                </div>
            </div>
        </div>
    </div>
</template>

<script>

import LoadingAnimation from '@/components/LoadingAnimation.vue';
import Notifications from '@/components/Notifications.vue';

// flows
import InitialFlow from '@/components/flows/InitialFlow.vue';
import RegistrationFlow from '@/components/flows/RegistrationFlow.vue';
import RegistrationFlowForMembers from '@/components/flows/RegistrationFlowForMembers.vue';
import VerificationFlow from '@/components/flows/VerificationFlow.vue';
import FinalFlow from '@/components/flows/FinalFlow.vue';

// to provide & inject values/functions across the whole app and nested components
import { provide } from 'vue'
import { ref } from 'vue'

import { setLocalStorage, getLocalStorage, getUrlParameters } from '@/storage-helper.js';

export default {
    name: 'Application',
    setup() {
        // with `ref` we can make any variable reactive
        const app = ref({});
        const processedApplication = ref({});

        const config = ref({
            lang: 'en',
            isLoading: false,
        });

        function updateApplication(obj) {
            app.value = { ...app.value, ...obj };
        }

        function updateApplicationField(key, value) {
            app.value[key] = value;
        }

        function persistLocally() {
            setLocalStorage(app.value);
        }

        // set forcePhoneVerification to app object 25% of the time
        // let random = Math.random() < 0.25;
        // console.log('s ::: ' + random);
        // updateApplicationField('forcePhoneVerification', random);
        updateApplicationField('forcePhoneVerification', true);

        function getTranslatedText(originalText = null, key=null) {
            if(!originalText) {
                return null;
            }
            // these translation_keys are being shared globally from DomainServiceProvider & added
            // to the window object on the layout blade file
            let keysToChange = window.App.translation_keys ?? {brand:'Loans Canada'};
            // need to wrap the text to be translated in double underscores like so: __requested_amount__
            // we can specify a key to be used in the translation file like so: this.getTranslatedText('__requested_amount__', 'steps.titles')
            let translated = originalText.replace(/__(.*?)__/, (match, stringToTranslate) => {
                return !key ? this.$t(stringToTranslate, keysToChange) : this.$t(key + "." + stringToTranslate, keysToChange);
            });
            return translated;
        }

        // If we want to send properties to child or even grandchild components, we can `provide` them
        // provide('app', app);
        provide('app', {
            app,
            config,
            updateApplication,
            updateApplicationField,
            persistLocally,
            processedApplication,
            getTranslatedText
        })

        // anything returned here will be available for the rest of the component
        return {
            app,
            config,
            updateApplicationField,
            getTranslatedText
        }
    },
    components: {
    LoadingAnimation,
    Notifications,
    InitialFlow,
    RegistrationFlow,
    RegistrationFlowForMembers,
    VerificationFlow,
    FinalFlow,
},
    props: {
        product: {
            type: String,
            required: false,
            default: 'personal_loan',
        },
        withMemberVerification: {
            type: Boolean,
            required: false,
            default: false,
        },
        skipVerification: {
            type: Boolean,
            required: false,
            default: false,
        },
        portalBaseUrl: {
            type: String,
            required: false,
            default: 'https://compare.loanscanada.ca'
        },
        prepopFields: {
            type: String,
            required: false,
            default: () => {
                return '';
            }
        },
        forceSmsVerification: {
            type: Boolean,
            required: false,
            default: () => {
                return false;
            }
        },
        affiliateLogoUrl: {
            type: String,
            required: false,
            default: () => {
                return null;
            }
        },
    },
    data: (props) => {
        // ApplicationSettings
        let applicationSettings = {

            portalBaseUrl: props.portalBaseUrl,
            mustRedirect: true,

            // bootstrap classes to change form/fields look
            formStyle: {
                buttonColor: {
                    next: 'primary',
                    skip: 'light',
                },
                inputColor: {
                    radio: 'primary'
                }
            },

            // switch to toggle general validation
            shouldValidateForm: true,

            progressBar: {
                active: true, // boolean
                isFinalStep: false,
                step: 0,
                valueNow: 0,
                // each step will add 7% or whatever number we set here. After 60% or when 100% is to be reached or
                // passed without being the actual end of the application process, this number will be ignored and
                // the progress bar will add at each following step 1/3 of the amount left to complete 100%
                stepProgress: 7,
            },

            // user/member status
            isUserRegistered: false,
            isUserAuthenticated: false,
            isUserVerified: false,

            // authentication
            userSkippedAuthentication: false,
            userFailedAuthentication: false,
            userAuthenticationAttempts: 0,

            // registration
            userWantsRegistration: false,
            userSkippedRegistration: false,
            userFailedRegistration: false,

            // verification
            userWantsVerification: false,
            userSkippedVerification: false,
            userFailedVerification: false,

            // loading animation
            showLoadingAnimation: false,
            loadingAnimationSeconds: 0,
            showLcLogoDuringAnimation: true,
            showAffiliateLogoDuringAnimation: false,

            // notifications
            notifications: {
                success: {},
                error: {},
                warning: {},
            },
            //
            completedApplication: false,
            partialRequestOnEveryStep: false,
            // stepOnFirstUserClick:
            // -- keeps the step number on the one a user clicks for the first time
            // -- set with @click event in the form (LcForm).
            // -- needed to determine to whether or not to show the "Continue you application..."
            // (showPrepopMessage) message when the Lead is coming from a prepop request (token is present)
            stepOnFirstUserClick: 0,
        };

        // InitialFlow
        let initialFlowSettings = {
            isVisible: true,
            isCompleted: false,
        };

        // RegistrationFlow
        let registrationFlowSettings = {
            isVisible: false,
            isCompleted: false,
        };

        let registrationFlowSettingsForMembers = {
            isVisible: false,
            isCompleted: false,
        };

        // VerificationFlow
        let verificationFlowSettings = {
            isVisible: false,
            isCompleted: false,
        };

        // FinalFlow
        let finalFlowSettings = {
            isVisible: false,
            isCompleted: false,
        }

        let projectBrand = null;

        return {
            applicationSettings,
            initialFlowSettings,
            registrationFlowSettings,
            registrationFlowSettingsForMembers,
            verificationFlowSettings,
            finalFlowSettings,
            projectBrand
        };
    },
    methods: {
        executeLoadingAnimation(time) {
            if (time < 0) {
                this.applicationSettings.showLoadingAnimation = true;
            }
            else if (time == 0) {
                this.applicationSettings.showLoadingAnimation = false;
                if(typeof timeoutId !== 'undefined' && timeoutId) {
                    clearTimeout(timeoutId);
                }
            }
            else {
                // loading animation is gonna be set to a maximum of 30sec
                let timeInSeconds = (time < 30) ? (time * 1000) : 30000;
                this.applicationSettings.showLoadingAnimation = true;
                let timeoutId = setTimeout(() => {
                    this.applicationSettings.showLoadingAnimation = false;
                }, timeInSeconds);
                // TODO -- set this ID so we can cancel it if needed
            }
        },
        // app data related functions
        setUrlParams() {
            let urlParameters = getUrlParameters();
            Object.assign(this.app, { urlParameters });
            this.prePopulateFields();
        },
        prePopulateFields() {
            let skipFields = [
                'partner_id'
            ];

            try {
                var prepopFieldsJson = JSON.parse(this.prepopFields);
                Object.entries(prepopFieldsJson).forEach(entry => {
                    if (!skipFields.includes(entry[0])) {
                        this.app[entry[0]] = entry[1];
                    }
                });
            } catch (error) {}

            Object.entries(this.app.urlParameters).forEach(entry => {
                if (!skipFields.includes(entry[0])) {
                    this.app[entry[0]] = entry[1];
                }
            });
        },
        getLocalData() {
            // console.log('getLocalData');
            let storedData = getLocalStorage();
            // TODO -- how are we gonna use this ?
        },
        checkEarlyRegistration() {
            if (this.app['email']) {
                let payload = {
                    email: this.app['email'],
                    password: 'fakePassword'
                };

                let validStatusCodes = [200, 401]; // 401 is to check existence only
                axios.post('/portal/member/login', payload)
                    .then(res => {
                        // console.log('checkEarlyRegistration', res.data);
                        if (!!res.data.statusCode && validStatusCodes.includes(res.data.statusCode)) {
                            this.applicationSettings.isUserRegistered = true;
                        }
                    }).catch(err => {
                        console.log(err)
                    });
            }
        },
    },
    computed: {
        showLcLogo() {
            return !!this.applicationSettings.showLcLogoDuringAnimation;
        },
        showAffiliateLogo() {
            return !!this.applicationSettings.showAffiliateLogoDuringAnimation;
        },
        loadingAnimation() {
            return !!this.applicationSettings.showLoadingAnimation;
        },
        showInitialFlow() {
            return !this.initialFlowSettings.isCompleted;
        },
        showRegistrationFlow() {
            return !!this.initialFlowSettings.isCompleted &&
                !this.registrationFlowSettings.isCompleted &&
                !this.applicationSettings.isUserRegistered;
                // (
                //     !this.applicationSettings.isUserRegistered ||
                //     !this.applicationSettings.isUserAuthenticated
                // );
        },
        showRegistrationFlowForMembers() {
            return !!this.initialFlowSettings.isCompleted &&
                !this.registrationFlowSettingsForMembers.isCompleted &&
                !!this.applicationSettings.isUserRegistered
        },
        showVerificationFlow() {
            return !!this.initialFlowSettings.isCompleted && (
                !!this.registrationFlowSettings.isCompleted || (!!this.applicationSettings.isUserRegistered && !!this.applicationSettings.isUserAuthenticated)
            ) &&
                !this.applicationSettings.userSkippedAuthentication &&
                !this.applicationSettings.userSkippedVerification &&
                !this.verificationFlowSettings.isCompleted;
        },
        showFinalFlow() {
            return (!this.showInitialFlow &&
                !this.showRegistrationFlow &&
                !this.showRegistrationFlowForMembers &&
                !this.showVerificationFlow) || !!this.applicationSettings.completedApplication;
        },
        showCompleteFlow() {
            return !!this.withMemberVerification;
        },
        showPrepopMessage() {
            let isFromRefreshResultsApp = this.app.hasOwnProperty('ra') ?? false;
            return !isFromRefreshResultsApp && this.prepopFields && this.prepopFields.length > 0 && (
                this.applicationSettings.stepOnFirstUserClick == 0 || this.applicationSettings.stepOnFirstUserClick == this.applicationSettings.progressBar.step
            );
        }
    },
    created() {
        // console.log('Application component created');
        this.setUrlParams();
        this.getLocalData();
        this.app['product_name'] = this.product;
    },
    mounted() {
        // console.log('Application component mounted');
        // this.checkEarlyRegistration();
        // google / sms verification
        // let isGoogle = this.app['utm_source'] == 'google';
        // this.updateApplicationField('forceSmsVerification', (isGoogle && this.forceSmsVerification));
        this.updateApplicationField('forceSmsVerification', this.forceSmsVerification);
        this.projectBrand = window.App.project_brand ?? [];
        this.updateApplicationField('experiments', window.App.experiments ?? null);
        this.updateApplicationField('projectBrandKey', this.projectBrand.key ?? 'lc');
    },
    watch: {
        "applicationSettings.loadingAnimationSeconds": {
            // immediate: true,
            handler(newValue) {
                // -1   => for unlimited animation when we dont know the duration of the request (max 30 seconds)
                // >0   => for animation during 'newValue' in seconds
                if (typeof newValue == 'number') {
                    this.executeLoadingAnimation(newValue);
                }
            }
        },
        "app.postal_code": {
            // immediate: true,
            handler(newValue) {
                if (newValue) {
                    if (!this.app.state) {
                        let code = newValue.trim().toUpperCase().charAt(0);
                        let postalCodeProvince = {
                            'A':'NL',
                            'B':'NS',
                            'C':'PE',
                            'E':'NB',
                            'G':'QC',
                            'H':'QC',
                            'J':'QC',
                            'K':'ON',
                            'L':'ON',
                            'M':'ON',
                            'N':'ON',
                            'P':'ON',
                            'R':'MB',
                            'S':'SK',
                            'T':'AB',
                            'V':'BC',
                            // 'X':'NU',
                            // 'X':'NT',
                            'Y':'YT',
                        };
                        this.updateApplicationField('state', postalCodeProvince[code] ?? null);
                    }
                }
            }
        }
    }

};
</script>
